import { useEffect, useRef } from 'react'

import { useNavOverlay } from 'Pods/UnifiedNavigation/Components/Navigation/Hooks/useNavOverlay'

// Open state for a single header action popover (cart / inbox / notifications),
// backed by the shared nav-overlay coordinator so only one nav overlay is open at
// a time. Keeps the close-on-outside-click and the App.events cart bridge on top.
function useActions(name) {
  const { isOpen, openId, open, close } = useNavOverlay(name)
  // Mirror of the shared open id for the once-registered document / App.events
  // listeners below, which capture their closures on mount.
  const stateRef = useRef(openId)
  const wrapperRef = useRef(null)
  const triggerRef = useRef(null)

  useEffect(() => {
    stateRef.current = openId
  }, [openId])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    /* eslint-disable-next-line no-undef */
    App.events.subscribe('top:menu:popover:hide', setInactive)
    App.events.subscribe('top:menu:cart:open', showCart)
    App.events.subscribe('top:menu:cart:close', hideCart)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      /* eslint-disable-next-line no-undef */
      const unsub = App.events.unsubscribe || App.events.remove
      unsub.call(App.events, 'top:menu:popover:hide', setInactive)
      unsub.call(App.events, 'top:menu:cart:open', showCart)
      unsub.call(App.events, 'top:menu:cart:close', hideCart)
    }
  }, [])

  function setInactive() {
    close(name)
  }

  function showCart() {
    // Only the cart action reacts; do nothing if it is already open.
    if (name !== 'cart-action' || stateRef.current === 'cart-action') {
      return
    }

    if (triggerRef.current) {
      // Click the trigger so the widget loads before the popover opens.
      triggerRef.current.click()
    } else {
      open('cart-action')
    }
  }

  function hideCart() {
    if (name === 'cart-action' && stateRef.current === 'cart-action') {
      close('cart-action')
    }
  }

  function handleClickOutside(e) {
    // Only the currently-open action closes itself on an outside click.
    if (name === stateRef.current) {
      const wrapper = wrapperRef.current
      const trigger = triggerRef.current

      if (trigger && wrapper && !trigger.contains(e.target) && !wrapper.contains(e.target)) {
        close(name)
      }
    }
  }

  return {
    isOpen,
    open,
    close,
    wrapperRef,
    triggerRef,
  }
}

export { useActions }
